import { Link } from 'react-router-dom';
import Layout from '../layout/Layout';
import { useI18n } from '../contexts/I18nContext';

export default function Legal() {
  const { legal, isRTL } = useI18n();
  const legalPages = [
    {
      id: 'privacy',
      title: legal.legal.pages.privacy.title,
      arabicTitle: legal.legal.pages.privacy.arabicTitle,
      description: legal.legal.pages.privacy.description,
      path: '/legal/privacy',
      icon: (
        <svg className="w-12 h-12 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      id: 'terms',
      title: legal.legal.pages.terms.title,
      arabicTitle: legal.legal.pages.terms.arabicTitle,
      description: legal.legal.pages.terms.description,
      path: '/legal/terms',
      icon: (
        <svg className="w-12 h-12 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'cookies',
      title: legal.legal.pages.cookies.title,
      arabicTitle: legal.legal.pages.cookies.arabicTitle,
      description: legal.legal.pages.cookies.description,
      path: '/legal/cookies',
      icon: (
        <svg className="w-12 h-12 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      id: 'licenses',
      title: legal.legal.pages.licenses.title,
      arabicTitle: legal.legal.pages.licenses.arabicTitle,
      description: legal.legal.pages.licenses.description,
      path: '/legal/licenses',
      icon: (
        <svg className="w-12 h-12 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F5FF] to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          {/* Header */}
          <div className="text-center mb-12 pt-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4">{legal.legal.mainPage.title}</h1>
            <p className="text-lg text-[#707070] max-w-2xl mx-auto">
              {legal.legal.mainPage.subtitle}
            </p>
            <p className="text-sm text-[#707070] mt-4">
              {legal.legal.mainPage.managedBy} <strong>{legal.legal.mainPage.company}</strong>
            </p>
          </div>

          {/* Cards Grid */}
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 mb-12">
            {legalPages.map((page) => (
              <Link
                key={page.id}
                to={page.path}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border-2 border-transparent hover:border-[#5B3FD0] group"
              >
                <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                  <div className="flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    {page.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-[#2C2C2C] mb-2 group-hover:text-[#5B3FD0] transition-colors">
                      {page.title}
                    </h2>
                    <p className="text-[#707070] leading-relaxed mb-3">
                      {page.description}
                    </p>
                    <div className={`flex items-center text-[#5B3FD0] font-semibold group-hover:${isRTL ? '-translate-x-2' : 'translate-x-2'} transition-transform duration-300`}>
                      <span>{legal.legal.pages.privacy.readMore}</span>
                      <svg className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Additional Information */}
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#5B3FD0]">
            <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4">{legal.legal.mainPage.needHelp.title}</h2>
            <p className="text-[#707070] leading-relaxed mb-4">
              {legal.legal.mainPage.needHelp.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:support@edunasser.com"
                className="inline-flex items-center px-6 py-3 bg-[#5B3FD0] text-white rounded-lg hover:bg-[#4A2FB0] transition-colors"
              >
                <svg className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {legal.legal.mainPage.needHelp.emailButton}
              </a>
              <a
                href="https://edunasser.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white text-[#5B3FD0] border-2 border-[#5B3FD0] rounded-lg hover:bg-[#F8F5FF] transition-colors"
              >
                <svg className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {legal.legal.mainPage.needHelp.websiteButton}
              </a>
            </div>
          </div>

          {/* Company Information */}
          <div className="max-w-4xl mx-auto mt-8 text-center">
            <p className="text-sm text-[#707070]">
              © {new Date().getFullYear()} {legal.legal.mainPage.footer.rights} <strong>{legal.legal.mainPage.company}</strong>. All rights reserved.
            </p>
            <p className="text-sm text-[#707070] mt-2">
              {legal.legal.mainPage.footer.lastUpdated} February 12, 2026
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}